// Canonical enumerations shared by the API (database enums, validation) and the web app (labels, badges).
// Each enum is a readonly tuple so it can feed both zod and Postgres enum definitions.

export const ROLE_KEYS = [
  'operations_coordinator',
  'shipping_operations_manager',
  'operations_supervisor',
  'finance_payment_processor',
  'executive_oversight',
  'system_administrator',
  'address_representative',
  'client_case_manager',
  'banker_call_specialist',
  'client',
] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];

export const ROLE_LABELS: Record<RoleKey, string> = {
  operations_coordinator: 'Operations Coordinator',
  shipping_operations_manager: 'Shipping Operations Manager',
  operations_supervisor: 'Operations Supervisor',
  finance_payment_processor: 'Finance Payment Processor',
  executive_oversight: 'Executive Oversight',
  system_administrator: 'System Administrator',
  address_representative: 'Address Representative',
  client_case_manager: 'Client Case Manager',
  banker_call_specialist: 'Banker Call Specialist',
  client: 'Client',
};

export const USER_STATUSES = ['invited', 'active', 'suspended'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const PRIORITIES = ['low', 'normal', 'high', 'critical'] as const;
export type Priority = (typeof PRIORITIES)[number];

// ---------- Tasks ----------
export const TASK_STATUSES = [
  'not_started',
  'in_progress',
  'waiting_external',
  'blocked',
  'under_review',
  'completed',
  'cancelled',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_CATEGORIES = [
  'shipment',
  'tracking_follow_up',
  'representative',
  'bank_call',
  'contract',
  'client',
  'payment',
  'ticket',
  'verification',
  'administrative',
  'client_service',
  'other',
] as const;
export type TaskCategory = (typeof TASK_CATEGORIES)[number];

// ---------- Shipments ----------
export const SHIPMENT_STATUSES = [
  'request_received',
  'pending_creation',
  'awaiting_representative',
  'label_created',
  'in_transit',
  'delivery_exception',
  'delivered_to_representative',
  'awaiting_forwarding',
  'in_transit_to_hub',
  'received_at_hub',
  'forwarded_to_client',
  'delivered_to_client',
  'closed',
  'cancelled',
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export const SHIPMENT_TYPES = ['mail', 'card', 'document', 'package', 'other'] as const;
export type ShipmentType = (typeof SHIPMENT_TYPES)[number];

// via_hub: sender -> representative -> Florida hub -> client (the usual route).
// two_leg: sender -> representative -> client directly.  inbound_only: held at representative.
// outbound_only: representative already holds the item and forwards it.
export const SHIPMENT_FLOWS = ['via_hub', 'two_leg', 'inbound_only', 'outbound_only'] as const;
export type ShipmentFlow = (typeof SHIPMENT_FLOWS)[number];

// inbound: sender -> representative; to_hub: representative -> Florida hub; outbound: to the client.
export const LEG_DIRECTIONS = ['inbound', 'to_hub', 'outbound'] as const;
export type LegDirection = (typeof LEG_DIRECTIONS)[number];

export const LEG_DIRECTION_LABELS: Record<LegDirection, string> = {
  inbound: 'Inbound · sender → representative',
  to_hub: 'To hub · representative → Florida hub',
  outbound: 'Outbound · to client',
};

export function legDirectionsForFlow(flow: ShipmentFlow): LegDirection[] {
  switch (flow) {
    case 'via_hub':
      return ['inbound', 'to_hub', 'outbound'];
    case 'two_leg':
      return ['inbound', 'outbound'];
    case 'inbound_only':
      return ['inbound'];
    case 'outbound_only':
      return ['outbound'];
  }
}

export const COURIERS = ['usps', 'ups', 'fedex', 'dhl', 'other'] as const;
export type Courier = (typeof COURIERS)[number];

// Normalized tracking status for a single courier journey (leg).
export const LEG_STATUSES = [
  'not_started',
  'pre_transit',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'failed_attempt',
  'exception',
  'returned',
  'lost',
  'unknown',
] as const;
export type LegStatus = (typeof LEG_STATUSES)[number];

// Normalized tracking event types produced by provider adapters.
export const TRACKING_EVENT_TYPES = [
  'label_created',
  'picked_up',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'failed_attempt',
  'address_problem',
  'refused',
  'returned',
  'lost',
  'held',
  'customs_delay',
  'exception',
  'info',
] as const;
export type TrackingEventType = (typeof TRACKING_EVENT_TYPES)[number];

export const EXCEPTION_EVENT_TYPES: readonly TrackingEventType[] = [
  'failed_attempt',
  'address_problem',
  'refused',
  'returned',
  'lost',
  'held',
  'customs_delay',
  'exception',
];

export const TRACKING_REGISTRATION_STATUSES = [
  'pending',
  'registered',
  'failed',
  'not_configured',
  'completed',
] as const;

export const TRACKING_EVENT_SOURCES = ['provider_webhook', 'provider_poll', 'manual'] as const;
export type TrackingEventSource = (typeof TRACKING_EVENT_SOURCES)[number];

// ---------- Addresses / representatives / contracts ----------
export const ADDRESS_STATUSES = [
  'available',
  'near_capacity',
  'full',
  'temporarily_unavailable',
  'inactive',
] as const;
export type AddressStatus = (typeof ADDRESS_STATUSES)[number];

export const CLIENT_TYPES = ['b2c', 'b2b'] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];

export const REPRESENTATIVE_STATUSES = ['active', 'onboarding', 'paused', 'inactive'] as const;

export const SERVICE_TYPES = [
  'mail_receiving',
  'card_receiving',
  'package_receiving',
  'forwarding',
  'address_rental',
  'other',
] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];

export const CONTRACT_STATUSES = [
  'draft',
  'sent_for_signature',
  'partially_signed',
  'executed',
  'expired',
  'terminated',
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

// ---------- Bank calls / communications ----------
export const BANK_CALL_STATUSES = [
  // Asked for (e.g. Arju → Paul for an Amex Global Transfer client) but not yet scheduled by the banker.
  'requested',
  'scheduled',
  'completed',
  'no_answer',
  'follow_up_required',
  'escalated',
  'cancelled',
] as const;

export const COMM_CHANNELS = ['whatsapp', 'email', 'phone', 'sms', 'portal', 'in_person', 'other'] as const;
export type CommChannel = (typeof COMM_CHANNELS)[number];

export const COMM_DIRECTIONS = ['inbound', 'outbound'] as const;

// ---------- Tickets ----------
export const TICKET_CATEGORIES = [
  'delayed_shipment',
  'lost_package',
  'courier_delivery_failure',
  'representative_unresponsive',
  'representative_refusing_forwarding',
  'incorrect_address',
  'missing_mail',
  'missing_card',
  'client_complaint',
  'contract_issue',
  'representative_payment_dispute',
  'courier_issue',
  // Raised by address representatives from the portal
  'incorrect_recipient',
  'missing_package',
  'label_problem',
  'dropoff_problem',
  'missing_receipt',
  'damaged_package',
  'scanning_issue',
  'general_assistance',
  // Raised by clients from the client portal
  'client_inquiry',
  'other',
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export const TICKET_STATUSES = [
  'open',
  'in_progress',
  'waiting_representative',
  'waiting_courier',
  'waiting_client',
  'pending_management',
  'resolved',
  'closed',
  'reopened',
] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

// Waiting statuses that may pause the resolution clock (with an explicit, logged pause).
export const SLA_PAUSABLE_TICKET_STATUSES: readonly TicketStatus[] = ['waiting_client', 'waiting_courier'];

export const ESCALATION_LEVELS = [1, 2, 3, 4] as const;
export type EscalationLevel = (typeof ESCALATION_LEVELS)[number];

export const ESCALATION_LEVEL_LABELS: Record<EscalationLevel, string> = {
  1: 'Level 1 · Coordinator',
  2: 'Level 2 · Shipping Manager',
  3: 'Level 3 · Operations Supervisor',
  4: 'Level 4 · Executive',
};

export const TICKET_MESSAGE_KINDS = ['internal_note', 'representative', 'client', 'courier', 'management_decision', 'system'] as const;
export type TicketMessageKind = (typeof TICKET_MESSAGE_KINDS)[number];

export const SLA_CLOCK_KINDS = ['response', 'resolution'] as const;
export const SLA_CLOCK_STATES = ['running', 'paused', 'met', 'breached', 'cancelled'] as const;

// ---------- Payments ----------
export const PAYMENT_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'correction_requested',
  'verified',
  'awaiting_approval',
  'approved',
  'rejected',
  'awaiting_payment',
  'paid',
  'disputed',
  // Operational payment requests (case manager → Jason/owner → Finance within 12 hours)
  'sent_to_finance',
  'processing',
  'paid_confirmation_pending',
  'completed',
  'overdue',
  'cancelled',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_APPROVAL_STEPS = ['submit', 'review', 'verify', 'authorize', 'reject', 'request_correction', 'process', 'dispute'] as const;

// ---------- Notifications ----------
export const NOTIFICATION_CATEGORIES = [
  'task_new',
  'task_overdue',
  'task_reassigned',
  'shipment_created',
  'label_purchased',
  'tracking_updated',
  'shipment_in_transit',
  'shipment_out_for_delivery',
  'shipment_delivered',
  'delivery_exception',
  'representative_response_required',
  'contract_expiring',
  'payment_review_required',
  'payment_authorized',
  'payment_paid',
  'ticket_created',
  'ticket_escalated',
  'ticket_assigned',
  'ticket_resolved',
  'ticket_reopened',
  'sla_warning',
  'daily_report_ready',
  'credit_low',
  'rep_mail',
  'rep_label',
  'rep_dropoff',
  'rep_settlement',
  'rep_message',
  'rep_reminder',
  'rep_expense',
  'client_quote',
  'service_update',
  'idpack',
  'opspay',
  'opspay_sla',
  'followup_due',
  'followup_escalated',
  'client_document',
  'client_inquiry',
  'invoice',
  'payout',
  'payout_sla',
  // WhatsApp agent: hand-offs and messages that need a person
  'whatsapp',
  // Dashboard announcements (Admin → Announcements)
  'announcement',
  // Client lease agreements ending soon / expired
  'lease_expiring',
  // Lease e-signing: viewed / signed / declined, and "your turn to countersign"
  'lease_signature',
  // Representative contract e-signing: viewed / signed / declined / executed, and "your turn to countersign"
  'contract_signature',
  'system',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_SEVERITIES = ['info', 'normal', 'urgent'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'sms', 'whatsapp', 'push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'skipped_not_configured', 'skipped_preference', 'expired'] as const;

export const REPORT_KINDS = ['employee_daily', 'department_daily', 'executive_weekly'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];
export const REPORT_STATUSES = ['draft', 'submitted', 'reviewed'] as const;

// ---------- Company finance ----------
export const FINANCE_DIRECTIONS = ['in', 'out'] as const;
export type FinanceDirection = (typeof FINANCE_DIRECTIONS)[number];
export const INCOME_CATEGORIES = ['client_payment', 'shipping_reimbursement', 'other_income'] as const;
export const EXPENSE_CATEGORIES = ['shipping', 'software', 'salaries', 'rep_payments', 'office_rent', 'apartment', 'advertising', 'bank_fees', 'client_refund', 'other_expense'] as const;
export const FINANCE_CATEGORIES = [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES] as const;
export type FinanceCategory = (typeof FINANCE_CATEGORIES)[number];
export const FINANCE_CATEGORY_LABELS: Record<FinanceCategory, string> = {
  client_payment: 'Client payment',
  shipping_reimbursement: 'Shipping reimbursement',
  other_income: 'Other income',
  shipping: 'Shipping costs',
  software: 'Software & subscriptions',
  salaries: 'Salaries',
  rep_payments: 'Representative payments',
  office_rent: 'Office rent',
  apartment: 'Apartment payments',
  advertising: 'Advertising',
  bank_fees: 'Bank & payment fees',
  client_refund: 'Client refunds',
  other_expense: 'Other expense',
};
export const PAYMENT_METHODS = ['bank_transfer', 'zelle', 'wire', 'card', 'cash', 'check', 'paypal', 'crypto', 'other'] as const;
export const CREDIT_TXN_TYPES = ['credit', 'usage'] as const;
export type CreditTxnType = (typeof CREDIT_TXN_TYPES)[number];

// ---------- Address representative portal ----------
export const REP_TICKET_CATEGORIES = [
  'missing_mail',
  'incorrect_recipient',
  'missing_package',
  'label_problem',
  'dropoff_problem',
  'missing_receipt',
  'damaged_package',
  'scanning_issue',
  'representative_payment_dispute',
  'general_assistance',
] as const;
export const MAIL_CATEGORIES = ['bank_card', 'bank_letter', 'government', 'irs_letter', 'business_document', 'personal_mail', 'package', 'other'] as const;
export type MailCategory = (typeof MAIL_CATEGORIES)[number];
export const MAIL_CATEGORY_LABELS: Record<MailCategory, string> = {
  bank_card: 'Bank card',
  bank_letter: 'Bank letter',
  government: 'Government correspondence',
  irs_letter: 'IRS letter',
  business_document: 'Business document',
  personal_mail: 'Personal mail',
  package: 'Package',
  other: 'Other',
};
export const MAIL_STATUSES = ['registered', 'scan_submitted', 'scan_approved', 'correction_requested', 'label_requested', 'label_ready', 'dropped_off', 'completed', 'held'] as const;
export type MailStatus = (typeof MAIL_STATUSES)[number];
export const SCAN_KINDS = ['envelope', 'contents'] as const;
export const SCAN_STATUSES = ['submitted', 'approved', 'correction_requested'] as const;
export type ScanStatus = (typeof SCAN_STATUSES)[number];
export const LABEL_REQUEST_STATUSES = ['requested', 'assigned', 'processing', 'awaiting_information', 'label_ready', 'completed', 'rejected', 'cancelled'] as const;
export type LabelRequestStatus = (typeof LABEL_REQUEST_STATUSES)[number];
export const LABEL_REQUEST_ACTIVE: LabelRequestStatus[] = ['requested', 'assigned', 'processing', 'awaiting_information', 'label_ready'];
export const DROPOFF_STATUSES = ['awaiting_dropoff', 'submitted', 'awaiting_verification', 'verified', 'correction_required', 'rejected'] as const;
export type DropoffStatus = (typeof DROPOFF_STATUSES)[number];
/** owner_review: Jason approved a settlement at or above the owner-approval threshold; the owner's final approval (TOTP) is next. */
export const SETTLEMENT_STATUSES = ['open', 'closed', 'requested', 'verified', 'owner_review', 'approved', 'paid', 'rejected'] as const;
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number];
/** Extra costs a representative pays out of pocket (reimbursed on the weekly settlement once approved). */
export const REP_EXPENSE_CATEGORIES = ['gas', 'postage_labels', 'supplies', 'parking_tolls', 'other'] as const;
export type RepExpenseCategory = (typeof REP_EXPENSE_CATEGORIES)[number];
export const REP_EXPENSE_CATEGORY_LABELS: Record<RepExpenseCategory, string> = {
  gas: 'Gas / mileage',
  postage_labels: 'Labels & postage',
  supplies: 'Supplies (envelopes, tape…)',
  parking_tolls: 'Parking & tolls',
  other: 'Other',
};
export const REP_EXPENSE_STATUSES = ['submitted', 'approved', 'rejected', 'cancelled'] as const;
export type RepExpenseStatus = (typeof REP_EXPENSE_STATUSES)[number];
// mail_received: per item received for a client (earned once its scan is approved); dropoff: per verified drop-off.
export const REP_SERVICE_TYPES = ['mail_received', 'dropoff', 'scan'] as const;
export const REP_SERVICE_LABELS: Record<(typeof REP_SERVICE_TYPES)[number], string> = { mail_received: 'Per item received (per client)', dropoff: 'Per verified drop-off', scan: 'Per approved scan' };
/** Common senders offered when registering mail (free text is allowed too). */
export const MAIL_SENDERS = [
  'Bank of America',
  'Chase',
  'Wells Fargo',
  'Citi',
  'Capital One',
  'U.S. Bank',
  'PNC',
  'Truist',
  'TD Bank',
  'American Express',
  'Discover',
  'Navy Federal',
  'Charles Schwab',
  'Mercury',
  'Relay',
  'Novo',
  'Bluevine',
  'IRS',
  'State government',
  'USPS',
  'Other',
] as const;
export type RepServiceType = (typeof REP_SERVICE_TYPES)[number];
export const CONVERSATION_TYPES = ['direct', 'shipment', 'label_request', 'dropoff', 'mail'] as const;
export type ConversationType = (typeof CONVERSATION_TYPES)[number];
export const CLOSEOUT_ITEMS = [
  { key: 'mail_registered', label: 'All received mail has been registered' },
  { key: 'scans_uploaded', label: 'Required scans are uploaded' },
  { key: 'labels_requested', label: 'Label requests have been submitted' },
  { key: 'labels_reviewed', label: 'Available labels have been reviewed' },
  { key: 'dropoffs_done', label: 'Required drop-offs are completed' },
  { key: 'receipts_uploaded', label: 'Drop-off receipts are uploaded' },
  { key: 'issues_reported', label: 'Outstanding issues have been reported' },
  { key: 'urgent_tasks', label: 'Urgent tasks have been addressed' },
] as const;
export type CloseoutKey = (typeof CLOSEOUT_ITEMS)[number]['key'];

/** Turns `snake_case` enum values into readable labels. */
export function humanize(value: string): string {
  const s = value.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------- Client shipment emails ----------
export const CLIENT_EMAIL_KINDS = ['to_hub_shipped', 'arrived_hub', 'quote', 'outbound_shipped', 'out_for_delivery', 'delivered'] as const;
export type ClientEmailKind = (typeof CLIENT_EMAIL_KINDS)[number];
export const CLIENT_EMAIL_KIND_LABELS: Record<ClientEmailKind, string> = {
  to_hub_shipped: 'On the way to the Florida facility',
  arrived_hub: 'Arrived at the Florida facility',
  quote: 'Shipping quote',
  outbound_shipped: 'Shipped to the client',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
};
export const CLIENT_EMAIL_STATUSES = ['pending', 'sent', 'failed', 'skipped', 'expired'] as const;
export const QUOTE_STATUSES = ['pending', 'approved', 'declined', 'cancelled', 'expired'] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];
